/**
 * API Client Service
 *
 * Handles all communication with the Content Processor API and backend services.
 * Implements authentication header injection from Auth0 session and typed error handling.
 *
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7, 11.1, 11.2, 11.4, 11.5
 */

import { config } from '../config';
import type {
  Document,
  DocumentMetadata,
  UploadedDocument,
  ExtractionResults,
  ProcessingJob,
  ProcessingStatus,
  FeedbackSubmission,
  Feedback,
} from '../types';
import type {
  SubmitResponse,
  StatusApiResponse,
  PaginatedApiResponse,
  ContentProcessResponse,
  ContentResultUpdate,
  ContentCommentUpdate,
  ContentResultDelete,
  SchemaResponse,
} from '../types/api';
import type {
  ProcessedDocument,
  PaginatedResponse,
  Schema,
} from '../types/documents';
import {
  parseContentProcessResponse,
  parsePaginatedResponse,
  parseProcessingStatus,
  parseStatusResponse,
  parseSchema,
} from './responseParser';

/**
 * API Error types for typed error handling
 */
export type ApiErrorType =
  | 'authentication'
  | 'authorization'
  | 'not_found'
  | 'validation'
  | 'file_too_large'
  | 'unsupported_type'
  | 'server_error'
  | 'network'
  | 'rate_limit'
  | 'timeout'
  | 'unknown';

/**
 * Typed API error response
 */
export interface ApiError {
  /** Type of error for programmatic handling */
  type: ApiErrorType;
  /** HTTP status code (if applicable) */
  status?: number;
  /** Human-readable error message */
  message: string;
  /** Additional error details (optional) */
  details?: Record<string, unknown>;
  /** Original error (for debugging) */
  originalError?: Error;
}

/**
 * API response wrapper for consistent error handling
 */
export type ApiResponse<T> = {
  success: true;
  data: T;
} | {
  success: false;
  error: ApiError;
};

/**
 * Map HTTP status codes to error types
 */
const mapStatusToErrorType = (status: number): ApiErrorType => {
  switch (status) {
    case 401:
      return 'authentication';
    case 403:
      return 'authorization';
    case 404:
      return 'not_found';
    case 400:
    case 422:
      return 'validation';
    case 413:
      return 'file_too_large';
    case 415:
      return 'unsupported_type';
    case 429:
      return 'rate_limit';
    case 500:
    case 502:
    case 503:
    case 504:
      return 'server_error';
    default:
      return 'unknown';
  }
};

/**
 * Create a typed API error from various error sources
 */
const createApiError = (
  type: ApiErrorType,
  message: string,
  status?: number,
  details?: Record<string, unknown>,
  originalError?: Error
): ApiError => ({
  type,
  status,
  message,
  details,
  originalError,
});

/**
 * Build request headers with optional authentication token
 */
const buildHeaders = (
  token?: string,
  additionalHeaders?: Record<string, string>
): HeadersInit => {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
};

/**
 * Build multipart form data headers with authentication
 */
const buildMultipartHeaders = (token?: string): HeadersInit => {
  const headers: Record<string, string> = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // Note: Don't set Content-Type for multipart/form-data
  // The browser will set it automatically with the boundary
  return headers;
};

/**
 * Handle fetch response and convert to typed result
 */
const handleResponse = async <T>(response: Response): Promise<ApiResponse<T>> => {
  if (response.ok) {
    try {
      const data = await response.json() as T;
      return { success: true, data };
    } catch {
      // Handle empty responses (e.g., 204 No Content)
      return { success: true, data: undefined as T };
    }
  }

  const errorType = mapStatusToErrorType(response.status);
  let message = `Request failed with status ${response.status}`;
  let details: Record<string, unknown> | undefined;

  try {
    const errorBody = await response.json();
    message = errorBody.message || errorBody.error || message;
    details = errorBody;
  } catch {
    // Response body is not JSON
  }

  return {
    success: false,
    error: createApiError(errorType, message, response.status, details),
  };
};

/**
 * Handle network errors
 */
const handleNetworkError = (error: Error): ApiResponse<never> => ({
  success: false,
  error: createApiError(
    'network',
    'Unable to connect to the server. Please check your internet connection.',
    undefined,
    undefined,
    error
  ),
});

/**
 * API Client class for Evidence Extractor
 *
 * Provides methods for:
 * - Document operations (upload, list, get, delete, download, version)
 * - Processing operations (process, status, results)
 * - Feedback operations (submit, history)
 */
export class ApiClient {
  private baseUrl: string;
  private getToken: () => Promise<string | undefined>;

  /**
   * Create a new API client instance
   *
   * @param getToken - Function to retrieve the current Auth0 access token
   * @param baseUrl - Optional base URL override (defaults to config.apiUrl)
   */
  constructor(
    getToken: () => Promise<string | undefined>,
    baseUrl?: string
  ) {
    this.baseUrl = baseUrl ?? config.evidenceExtractorApiUrl;
    this.getToken = getToken;
  }

  /**
   * Make an authenticated request to the API
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}${endpoint}`;

      const response = await fetch(url, {
        ...options,
        headers: {
          ...buildHeaders(token),
          ...options.headers,
        },
      });

      return handleResponse<T>(response);
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Make an authenticated multipart request (for file uploads)
   */
  private async multipartRequest<T>(
    endpoint: string,
    formData: FormData
  ): Promise<ApiResponse<T>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}${endpoint}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: buildMultipartHeaders(token),
        body: formData,
      });

      return handleResponse<T>(response);
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  // ============================================
  // Document Operations
  // ============================================

  /**
   * Upload a new document
   *
   * @param file - The file to upload
   * @param metadata - Document metadata (name, classification)
   * @returns The uploaded document or an error
   */
  async uploadDocument(
    file: File,
    metadata: DocumentMetadata
  ): Promise<ApiResponse<UploadedDocument>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', metadata.name);
    formData.append('classification', metadata.classification);

    return this.multipartRequest<UploadedDocument>('/api/documents', formData);
  }

  /**
   * Get all documents for the current user
   *
   * @returns List of documents or an error
   */
  async getDocuments(): Promise<ApiResponse<Document[]>> {
    return this.request<Document[]>('/api/documents', {
      method: 'GET',
    });
  }

  /**
   * Get a specific document by ID
   *
   * @param id - Document ID
   * @returns The document or an error
   */
  async getDocument(id: string): Promise<ApiResponse<Document>> {
    return this.request<Document>(`/api/documents/${encodeURIComponent(id)}`, {
      method: 'GET',
    });
  }

  /**
   * Delete a document
   *
   * @param id - Document ID to delete
   * @returns Success or an error
   */
  async deleteDocument(id: string): Promise<ApiResponse<void>> {
    return this.request<void>(`/api/documents/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  }

  /**
   * Download a document
   *
   * @param id - Document ID to download
   * @returns The document blob or an error
   */
  async downloadDocument(id: string): Promise<ApiResponse<Blob>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}/api/documents/${encodeURIComponent(id)}/download`;

      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const blob = await response.blob();
        return { success: true, data: blob };
      }

      const errorType = mapStatusToErrorType(response.status);
      return {
        success: false,
        error: createApiError(
          errorType,
          `Failed to download document: ${response.statusText}`,
          response.status
        ),
      };
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Upload a new version of an existing document
   *
   * @param id - Document ID to update
   * @param file - The new file version
   * @returns The updated document or an error
   */
  async uploadDocumentVersion(
    id: string,
    file: File
  ): Promise<ApiResponse<Document>> {
    const formData = new FormData();
    formData.append('file', file);

    return this.multipartRequest<Document>(
      `/api/documents/${encodeURIComponent(id)}/versions`,
      formData
    );
  }

  // ============================================
  // Processing Operations
  // ============================================

  /**
   * Start processing documents for SORA evidence extraction
   *
   * @param documentIds - Array of document IDs to process
   * @returns The processing job or an error
   */
  async processDocuments(
    documentIds: string[]
  ): Promise<ApiResponse<ProcessingJob>> {
    return this.request<ProcessingJob>('/api/processing', {
      method: 'POST',
      body: JSON.stringify({ documentIds }),
    });
  }

  /**
   * Get the status of a processing job
   *
   * @param jobId - Processing job ID
   * @returns The processing status or an error
   */
  async getProcessingStatus(
    jobId: string
  ): Promise<ApiResponse<ProcessingStatus>> {
    return this.request<ProcessingStatus>(
      `/api/processing/${encodeURIComponent(jobId)}/status`,
      { method: 'GET' }
    );
  }

  /**
   * Get extraction results for a completed processing job
   *
   * @param processId - Process ID from the completed job
   * @returns The extraction results or an error
   */
  async getExtractionResults(
    processId: string
  ): Promise<ApiResponse<ExtractionResults>> {
    return this.request<ExtractionResults>(
      `/api/extractions/${encodeURIComponent(processId)}`,
      { method: 'GET' }
    );
  }

  // ============================================
  // Feedback Operations
  // ============================================

  /**
   * Submit feedback for an extracted evidence item
   *
   * @param extractionId - ID of the extraction
   * @param feedback - Feedback submission data
   * @returns Success or an error
   */
  async submitFeedback(
    extractionId: string,
    feedback: FeedbackSubmission
  ): Promise<ApiResponse<void>> {
    return this.request<void>(
      `/api/extractions/${encodeURIComponent(extractionId)}/feedback`,
      {
        method: 'POST',
        body: JSON.stringify(feedback),
      }
    );
  }

  /**
   * Get feedback history for an extraction
   *
   * @param extractionId - ID of the extraction
   * @returns List of feedback records or an error
   */
  async getFeedbackHistory(
    extractionId: string
  ): Promise<ApiResponse<Feedback[]>> {
    return this.request<Feedback[]>(
      `/api/extractions/${encodeURIComponent(extractionId)}/feedback`,
      { method: 'GET' }
    );
  }

  /**
   * Submit batch feedback for multiple items
   *
   * @param extractionId - ID of the extraction
   * @param feedbackItems - Array of feedback submissions
   * @returns Success or an error
   */
  async submitBatchFeedback(
    extractionId: string,
    feedbackItems: FeedbackSubmission[]
  ): Promise<ApiResponse<void>> {
    return this.request<void>(
      `/api/extractions/${encodeURIComponent(extractionId)}/feedback/batch`,
      {
        method: 'POST',
        body: JSON.stringify({ items: feedbackItems }),
      }
    );
  }

  // ============================================
  // ContentProcessorAPI Operations
  // Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7
  // ============================================

  /**
   * Submit a document for processing via ContentProcessorAPI
   *
   * Sends a POST request to /contentprocessor/submit with multipart form data
   * containing the file and a JSON-encoded data object with Schema_Id and Metadata_Id.
   *
   * @param file - The file to submit for processing
   * @param schemaId - The schema ID to use for extraction
   * @param metadataId - The metadata ID for the document
   * @returns SubmitResponse with message and status_url for polling
   *
   * Requirement 1.1: POST to /contentprocessor/submit with multipart form
   */
  async submitDocument(
    file: File,
    schemaId: string,
    metadataId: string
  ): Promise<ApiResponse<SubmitResponse>> {
    const formData = new FormData();
    formData.append('file', file);
    // API expects ContentProcessorRequest as a JSON string in the 'data' field
    formData.append('data', JSON.stringify({
      Schema_Id: schemaId,
      Metadata_Id: metadataId,
    }));

    return this.multipartRequest<SubmitResponse>(
      '/contentprocessor/submit',
      formData
    );
  }

  /**
   * Get paginated list of processed documents from ContentProcessorAPI
   *
   * Sends a POST request to /contentprocessor/processed with pagination
   * parameters in the request body.
   *
   * @param pageNumber - The page number to retrieve (1-indexed)
   * @param pageSize - The number of items per page
   * @returns PaginatedResponse containing ProcessedDocument items
   *
   * Requirement 1.2: POST to /contentprocessor/processed with pagination body
   */
  async getProcessedDocuments(
    pageNumber: number,
    pageSize: number
  ): Promise<ApiResponse<PaginatedResponse<ProcessedDocument>>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}/contentprocessor/processed`;

      const response = await fetch(url, {
        method: 'POST',
        headers: buildHeaders(token),
        body: JSON.stringify({
          page_number: pageNumber,
          page_size: pageSize,
        }),
      });

      if (response.ok) {
        const data = await response.json() as PaginatedApiResponse;
        const parsed = parsePaginatedResponse(data, parseContentProcessResponse);
        return { success: true, data: parsed };
      }

      const errorType = mapStatusToErrorType(response.status);
      let message = `Request failed with status ${response.status}`;
      let details: Record<string, unknown> | undefined;

      try {
        const errorBody = await response.json();
        message = errorBody.message || errorBody.error || message;
        details = errorBody;
      } catch {
        // Response body is not JSON
      }

      return {
        success: false,
        error: createApiError(errorType, message, response.status, details),
      };
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Get processing status for a document from ContentProcessorAPI
   *
   * Sends a GET request to /contentprocessor/status/{process_id} and interprets
   * the HTTP status code to determine processing state:
   * - 200: processing (document is still being processed)
   * - 302: completed (processing finished successfully)
   * - 404: not found (process ID does not exist)
   * - 500: error (processing failed)
   *
   * @param processId - The process ID to check status for
   * @returns StatusResponse with status, message, and optional resourceUrl
   *
   * Requirement 1.3: GET /contentprocessor/status/{process_id} with status code handling
   */
  async getContentProcessingStatus(
    processId: string
  ): Promise<ApiResponse<{ status: ProcessingStatus; message: string; resourceUrl?: string }>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}/contentprocessor/status/${encodeURIComponent(processId)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        redirect: 'follow', // Follow redirects — we parse the body status field instead
      });

      // Parse response body if available
      let responseBody: StatusApiResponse | undefined;
      try {
        responseBody = await response.json() as StatusApiResponse;
      } catch {
        // Response body may not be JSON
      }

      // For 404 and 500, return as error using status code
      if (response.status === 404) {
        const parsed = parseStatusResponse(response.status, responseBody);
        return {
          success: false,
          error: createApiError('not_found', parsed.message, response.status),
        };
      }

      if (response.status >= 500) {
        const parsed = parseStatusResponse(response.status, responseBody);
        return {
          success: false,
          error: createApiError('server_error', parsed.message, response.status),
        };
      }

      // For 200/302/other success codes, determine status from the response body.
      // The backend returns {"status": "completed"} for finished documents (HTTP 302 which
      // may be followed as a redirect) and {"status": "<step_name>"} for in-progress (HTTP 200).
      if (responseBody?.status) {
        const parsedStatus = parseProcessingStatus(responseBody.status);
        return {
          success: true,
          data: {
            status: parsedStatus,
            message: responseBody.message ?? 'Processing',
            resourceUrl: responseBody.resource_url,
          },
        };
      }

      // Fallback: use HTTP status code mapping
      const parsed = parseStatusResponse(response.status, responseBody);
      return { success: true, data: parsed };
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Get a single processed document by process ID from ContentProcessorAPI
   *
   * Sends a GET request to /contentprocessor/processed/{process_id} to retrieve
   * the full details of a processed document including extraction results.
   *
   * @param processId - The process ID of the document to retrieve
   * @returns ProcessedDocument with full extraction details
   *
   * Requirement 1.4: GET /contentprocessor/processed/{process_id}
   */
  async getProcessedDocument(
    processId: string
  ): Promise<ApiResponse<ProcessedDocument>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}/contentprocessor/processed/${encodeURIComponent(processId)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const data = await response.json() as ContentProcessResponse;
        const parsed = parseContentProcessResponse(data);
        return { success: true, data: parsed };
      }

      const errorType = mapStatusToErrorType(response.status);
      let message = `Request failed with status ${response.status}`;
      let details: Record<string, unknown> | undefined;

      try {
        const errorBody = await response.json();
        message = errorBody.message || errorBody.error || message;
        details = errorBody;
      } catch {
        // Response body is not JSON
      }

      return {
        success: false,
        error: createApiError(errorType, message, response.status, details),
      };
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Delete a processed document from ContentProcessorAPI
   *
   * Sends a DELETE request to /contentprocessor/processed/{process_id} to
   * remove a processed document and its associated data.
   *
   * @param processId - The process ID of the document to delete
   * @returns ContentResultDelete with deletion confirmation
   *
   * Requirement 1.6: DELETE /contentprocessor/processed/{process_id}
   */
  async deleteProcessedDocument(
    processId: string
  ): Promise<ApiResponse<ContentResultDelete>> {
    return this.request<ContentResultDelete>(
      `/contentprocessor/processed/${encodeURIComponent(processId)}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Stream the original file for a processed document from ContentProcessorAPI
   *
   * Sends a GET request to /contentprocessor/processed/files/{process_id} to
   * download the original file that was submitted for processing.
   *
   * @param processId - The process ID of the document to download
   * @returns Blob containing the original file data
   *
   * Requirement 1.5: GET /contentprocessor/processed/files/{process_id}
   */
  async streamOriginalFile(
    processId: string
  ): Promise<ApiResponse<Blob>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}/contentprocessor/processed/files/${encodeURIComponent(processId)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const blob = await response.blob();
        return { success: true, data: blob };
      }

      const errorType = mapStatusToErrorType(response.status);
      return {
        success: false,
        error: createApiError(
          errorType,
          `Failed to download file: ${response.statusText}`,
          response.status
        ),
      };
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Update a processed document in ContentProcessorAPI
   *
   * Sends a PUT request to /contentprocessor/processed/{process_id} with either
   * a ContentResultUpdate (to update extraction results) or ContentCommentUpdate
   * (to update comments).
   *
   * @param processId - The process ID of the document to update
   * @param update - Either ContentResultUpdate or ContentCommentUpdate payload
   * @returns Updated ProcessedDocument
   *
   * Requirement 1.7: PUT /contentprocessor/processed/{process_id}
   */
  async updateProcessedDocument(
    processId: string,
    update: ContentResultUpdate | ContentCommentUpdate
  ): Promise<ApiResponse<ProcessedDocument>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}/contentprocessor/processed/${encodeURIComponent(processId)}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: buildHeaders(token),
        body: JSON.stringify(update),
      });

      if (response.ok) {
        const data = await response.json() as ContentProcessResponse;
        const parsed = parseContentProcessResponse(data);
        return { success: true, data: parsed };
      }

      const errorType = mapStatusToErrorType(response.status);
      let message = `Request failed with status ${response.status}`;
      let details: Record<string, unknown> | undefined;

      try {
        const errorBody = await response.json();
        message = errorBody.message || errorBody.error || message;
        details = errorBody;
      } catch {
        // Response body is not JSON
      }

      return {
        success: false,
        error: createApiError(errorType, message, response.status, details),
      };
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  // ============================================
  // Schema Vault Operations
  // Requirements: 2.1, 2.3
  // ============================================

  /**
   * Get all available schemas from Schema Vault
   *
   * Sends a GET request to /schemavault/ to retrieve the list of
   * available extraction schemas.
   *
   * @returns Array of Schema objects
   *
   * Requirement 2.1: GET /schemavault/
   */
  async getSchemas(): Promise<ApiResponse<Schema[]>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}/schemavault/`;

      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const data = await response.json() as SchemaResponse[];
        const parsed = data.map(parseSchema);
        return { success: true, data: parsed };
      }

      const errorType = mapStatusToErrorType(response.status);
      let message = `Request failed with status ${response.status}`;
      let details: Record<string, unknown> | undefined;

      try {
        const errorBody = await response.json();
        message = errorBody.message || errorBody.error || message;
        details = errorBody;
      } catch {
        // Response body is not JSON
      }

      return {
        success: false,
        error: createApiError(errorType, message, response.status, details),
      };
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }

  /**
   * Get a specific schema by ID from Schema Vault
   *
   * Sends a GET request to /schemavault/schemas/{schema_id} to retrieve
   * the details of a specific extraction schema.
   *
   * @param schemaId - The schema ID to retrieve
   * @returns Schema object with full details
   *
   * Requirement 2.3: GET /schemavault/schemas/{schema_id}
   */
  async getSchema(schemaId: string): Promise<ApiResponse<Schema>> {
    try {
      const token = await this.getToken();
      const url = `${this.baseUrl}/schemavault/schemas/${encodeURIComponent(schemaId)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.ok) {
        const data = await response.json() as SchemaResponse;
        const parsed = parseSchema(data);
        return { success: true, data: parsed };
      }

      const errorType = mapStatusToErrorType(response.status);
      let message = `Request failed with status ${response.status}`;
      let details: Record<string, unknown> | undefined;

      try {
        const errorBody = await response.json();
        message = errorBody.message || errorBody.error || message;
        details = errorBody;
      } catch {
        // Response body is not JSON
      }

      return {
        success: false,
        error: createApiError(errorType, message, response.status, details),
      };
    } catch (error) {
      if (error instanceof Error) {
        return handleNetworkError(error);
      }
      return {
        success: false,
        error: createApiError('unknown', 'An unexpected error occurred'),
      };
    }
  }
}

/**
 * Create an API client instance with Auth0 token retrieval
 *
 * @param getAccessTokenSilently - Auth0 getAccessTokenSilently function
 * @returns Configured API client instance
 */
export const createApiClient = (
  getAccessTokenSilently: () => Promise<string>
): ApiClient => {
  return new ApiClient(async () => {
    try {
      return await getAccessTokenSilently();
    } catch {
      // Return undefined if token retrieval fails
      // The API will return 401 and the error handler will redirect to login
      return undefined;
    }
  });
};

/**
 * Create an API client for unauthenticated requests (demo mode)
 *
 * @returns API client without authentication
 */
export const createUnauthenticatedApiClient = (): ApiClient => {
  return new ApiClient(async () => undefined);
};

/**
 * Create an API client for unauthenticated requests (demo mode)
 *
 * @returns API client without authentication
 */
export const createPolicyNavigatorApiClient = (
  getAccessTokenSilently: () => Promise<string>
): ApiClient => {
  return new ApiClient(async () => {
    try {
      return await getAccessTokenSilently();
    } catch {
      // Return undefined if token retrieval fails
      // The API will return 401 and the error handler will redirect to login
      return undefined;
    }
  }, config.documentServiceApiUrl);
};

/**
 * Type guard to check if an API response is successful
 */
export const isApiSuccess = <T>(
  response: ApiResponse<T>
): response is { success: true; data: T } => {
  return response.success === true;
};

/**
 * Type guard to check if an API response is an error
 */
export const isApiError = <T>(
  response: ApiResponse<T>
): response is { success: false; error: ApiError } => {
  return response.success === false;
};

/**
 * Get user-friendly error message based on error type
 */
export const getErrorMessage = (error: ApiError): string => {
  switch (error.type) {
    case 'authentication':
      return 'Your session has expired. Please log in again.';
    case 'authorization':
      return 'You do not have permission to perform this action. Please contact support if you believe this is an error.';
    case 'not_found':
      return 'The requested resource was not found. It may have been moved or deleted.';
    case 'validation':
      return error.message || 'Please check your input and try again.';
    case 'file_too_large':
      return 'File size exceeds the 20MB limit. Please upload a smaller file.';
    case 'unsupported_type':
      return 'Only PDF, JPEG, and PNG files are supported. Please upload a supported file type.';
    case 'rate_limit':
      return 'Too many requests. Please wait a moment and try again.';
    case 'server_error':
      return 'A server error occurred. Our team has been notified. Please try again later.';
    case 'network':
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    case 'timeout':
      return 'Processing is taking longer than expected. Please try again later.';
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
};

/**
 * Get error title based on error type
 */
export const getErrorTitle = (error: ApiError): string => {
  switch (error.type) {
    case 'authentication':
      return 'Session Expired';
    case 'authorization':
      return 'Access Denied';
    case 'not_found':
      return 'Not Found';
    case 'validation':
      return 'Validation Error';
    case 'file_too_large':
      return 'File Too Large';
    case 'unsupported_type':
      return 'Unsupported File Type';
    case 'rate_limit':
      return 'Too Many Requests';
    case 'server_error':
      return 'Server Error';
    case 'network':
      return 'Connection Error';
    case 'timeout':
      return 'Request Timeout';
    default:
      return 'Error';
  }
};

/**
 * Check if error requires user to re-authenticate
 */
export const requiresReauth = (error: ApiError): boolean => {
  return error.type === 'authentication';
};

/**
 * Check if error is retryable
 */
export const isRetryableError = (error: ApiError): boolean => {
  return ['network', 'server_error', 'rate_limit'].includes(error.type);
};

// ============================================
// Retry Logic Helpers
// Requirements: 9.6, 9.7
// ============================================

/**
 * Configuration for retry behavior
 */
export interface RetryConfig {
  /** Maximum number of retry attempts (default: 3) */
  maxRetries: number;
  /** Initial delay in milliseconds before first retry (default: 1000) */
  initialDelayMs: number;
  /** Maximum delay in milliseconds between retries (default: 30000) */
  maxDelayMs: number;
  /** Multiplier for exponential backoff (default: 2) */
  backoffMultiplier: number;
  /** Optional callback for retry progress */
  onRetry?: (attempt: number, delay: number, error: ApiError) => void;
}

/**
 * Default retry configuration
 */
export const DEFAULT_RETRY_CONFIG: RetryConfig = {
  maxRetries: 3,
  initialDelayMs: 1000,
  maxDelayMs: 30000,
  backoffMultiplier: 2,
};

/**
 * Calculate delay for a given retry attempt using exponential backoff
 *
 * Formula: min(initialDelay * backoffMultiplier^attempt, maxDelay)
 *
 * @param attempt - The current retry attempt (0-indexed)
 * @param config - Retry configuration
 * @returns Delay in milliseconds
 */
export const calculateRetryDelay = (
  attempt: number,
  config: Partial<RetryConfig> = {}
): number => {
  const {
    initialDelayMs = DEFAULT_RETRY_CONFIG.initialDelayMs,
    maxDelayMs = DEFAULT_RETRY_CONFIG.maxDelayMs,
    backoffMultiplier = DEFAULT_RETRY_CONFIG.backoffMultiplier,
  } = config;

  const delay = initialDelayMs * Math.pow(backoffMultiplier, attempt);
  return Math.min(delay, maxDelayMs);
};

/**
 * Sleep for a specified duration
 *
 * @param ms - Duration in milliseconds
 * @returns Promise that resolves after the delay
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Extract Retry-After header value from an API error
 *
 * The Retry-After header can be either:
 * - A number of seconds to wait
 * - An HTTP-date indicating when to retry
 *
 * @param error - The API error that may contain Retry-After information
 * @returns Delay in milliseconds, or undefined if not present
 */
export const getRetryAfterDelay = (error: ApiError): number | undefined => {
  const retryAfter = error.details?.['retry-after'] || error.details?.['Retry-After'];

  if (typeof retryAfter === 'number') {
    return retryAfter * 1000; // Convert seconds to milliseconds
  }

  if (typeof retryAfter === 'string') {
    // Try parsing as number of seconds
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds)) {
      return seconds * 1000;
    }

    // Try parsing as HTTP-date
    const date = new Date(retryAfter);
    if (!isNaN(date.getTime())) {
      const delay = date.getTime() - Date.now();
      return delay > 0 ? delay : undefined;
    }
  }

  return undefined;
};

/**
 * Retry result type
 */
export type RetryResult<T> = {
  success: true;
  data: T;
  attempts: number;
} | {
  success: false;
  error: ApiError;
  attempts: number;
};

/**
 * Execute an API operation with automatic retry logic for retryable errors
 *
 * This function wraps any async operation that returns an ApiResponse and
 * automatically retries on retryable errors (network, server_error, rate_limit)
 * using exponential backoff.
 *
 * For rate_limit errors, it respects the Retry-After header if present.
 *
 * @param operation - The async operation to execute
 * @param config - Optional retry configuration
 * @returns The operation result with retry metadata
 *
 * Requirements: 9.6, 9.7
 *
 * @example
 * ```typescript
 * const result = await retryRequest(
 *   () => apiClient.getProcessedDocuments(1, 10),
 *   { maxRetries: 3, onRetry: (attempt, delay) => console.log(`Retry ${attempt} in ${delay}ms`) }
 * );
 * ```
 */
export const retryRequest = async <T>(
  operation: () => Promise<ApiResponse<T>>,
  config: Partial<RetryConfig> = {}
): Promise<RetryResult<T>> => {
  const {
    maxRetries = DEFAULT_RETRY_CONFIG.maxRetries,
    onRetry,
  } = { ...DEFAULT_RETRY_CONFIG, ...config };

  let lastError: ApiError | undefined;
  let attempts = 0;

  while (attempts <= maxRetries) {
    attempts++;

    const result = await operation();

    if (result.success) {
      return {
        success: true,
        data: result.data,
        attempts,
      };
    }

    lastError = result.error;

    // Check if error is retryable and we have retries left
    if (!isRetryableError(result.error) || attempts > maxRetries) {
      break;
    }

    // Calculate delay - use Retry-After header for rate limit errors if available
    let delay: number;
    if (result.error.type === 'rate_limit') {
      const retryAfterDelay = getRetryAfterDelay(result.error);
      delay = retryAfterDelay ?? calculateRetryDelay(attempts - 1, config);
    } else {
      delay = calculateRetryDelay(attempts - 1, config);
    }

    // Notify about retry if callback provided
    if (onRetry) {
      onRetry(attempts, delay, result.error);
    }

    // Wait before retrying
    await sleep(delay);
  }

  return {
    success: false,
    error: lastError ?? createApiError('unknown', 'Retry failed with no error'),
    attempts,
  };
};

/**
 * Create a retryable version of an API client method
 *
 * This is a higher-order function that wraps an API method with retry logic.
 *
 * @param method - The API method to wrap
 * @param config - Optional retry configuration
 * @returns A wrapped method that automatically retries on retryable errors
 *
 * @example
 * ```typescript
 * const retryableGetDocuments = withRetry(
 *   (page: number, size: number) => apiClient.getProcessedDocuments(page, size),
 *   { maxRetries: 3 }
 * );
 * const result = await retryableGetDocuments(1, 10);
 * ```
 */
export const withRetry = <TArgs extends unknown[], TResult>(
  method: (...args: TArgs) => Promise<ApiResponse<TResult>>,
  config: Partial<RetryConfig> = {}
): ((...args: TArgs) => Promise<RetryResult<TResult>>) => {
  return (...args: TArgs) => retryRequest(() => method(...args), config);
};

/**
 * Check if a retry result indicates all retries were exhausted
 */
export const isRetriesExhausted = <T>(
  result: RetryResult<T>,
  config: Partial<RetryConfig> = {}
): boolean => {
  const maxRetries = config.maxRetries ?? DEFAULT_RETRY_CONFIG.maxRetries;
  return !result.success && result.attempts > maxRetries;
};

/**
 * Convert a RetryResult back to a standard ApiResponse
 *
 * Useful when you need to pass the result to code that expects ApiResponse
 */
export const toApiResponse = <T>(result: RetryResult<T>): ApiResponse<T> => {
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
};

export default ApiClient;
